import type { FC } from 'react';
import { useFormik } from 'formik';
import { Button } from '@selfinspectionorg/ui-kit';
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import styles from './NewPasswordForm.module.scss';
import { validationSchema } from './form/validation.schema';
import { type NewPasswordFormValues } from './form/formValue.type';
import { Card } from '@/components/ui';
import { PasswordInput } from '@/components/form/PasswordInput';
import { PasswordRequirements } from '@/components/form/PasswordRequirements';
import { useCreateNewPasswordMutation } from '@/shared/api/auth';
import { getApiErrorString } from '@/shared/utils';

interface Props {
  /** The reset-link email (from the URL, or the forgot-password wizard's state). */
  email: string;
  /** The confirmed recovery code. */
  code: string;
}

/**
 * The password-recovery "set a new password" step. Reached two ways with identical behavior: the
 * `/new-password` email link (email + code in the URL) and the final step of the `/forgot-password`
 * wizard (email + confirmed code from its state). Owns its own Card so both callers render it without
 * nesting.
 */
export const SetNewPasswordForm: FC<Props> = ({ email, code }) => {
  const navigate = useNavigate();

  const { createNewPassword, isCreatingNewPassword, createNewPasswordError } = useCreateNewPasswordMutation();
  const [isNewPasswordCreated, setIsNewPasswordCreated] = useState<boolean>(false);

  const form = useFormik<NewPasswordFormValues>({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validationSchema,
    validateOnChange: false,
    onSubmit: async (values) => {
      await createNewPassword({ email, newPassword: values.password, recoveryCode: code });
      setIsNewPasswordCreated(true);
    },
  });

  const apiError = createNewPasswordError ? getApiErrorString(createNewPasswordError) : undefined;

  if (isNewPasswordCreated) {
    return (
      <Card>
        <Card.Title title="Success!" description="Your new password has been created." />
        <Button width="100%" onClick={() => navigate({ to: '/login' })}>
          Back to Login
        </Button>
      </Card>
    );
  }

  return (
    <Card className={styles.wrapper}>
      <Card.Title title="Create new password" />
      <form>
        <PasswordInput
          label="New password"
          required
          name="password"
          placeholder="Enter new password"
          value={form.values.password}
          onChange={form.handleChange}
          errored={!!form.errors.password}
          testId="new-password-input"
        />
        <PasswordRequirements value={form.values.password} />
        <PasswordInput
          label="Confirm password"
          required
          name="confirmPassword"
          placeholder="Confirm your password"
          value={form.values.confirmPassword}
          onChange={form.handleChange}
          errored={!!form.errors.confirmPassword || !!apiError}
          helperMessage={form.errors.confirmPassword || apiError}
          testId="confirm-password-input"
        />
        <div className={styles.buttonContainer}>
          <Button
            onClick={form.submitForm}
            width="100%"
            isLoading={isCreatingNewPassword}
            data-testid="create-password-button"
          >
            Create password
          </Button>
          <Button.Ghost onClick={() => navigate({ to: '/login', replace: true })} data-testid="back-to-login-button">
            Back to login
          </Button.Ghost>
        </div>
      </form>
    </Card>
  );
};
